import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { movies } from "@/mocks/movies";
import { articles } from "@/mocks/news";

const principles = [
  {
    icon: "ri-book-read-line",
    title: "事实先行",
    text: "每部影片先交代年份、导演、主演、片长与类型，再进入剧情与观点。基础资料尽量可核对，不为了好看而虚构。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "观点有据",
    text: "影评只代表作者个人判断。我们要求每一段评价都能在影片里找到依据，而不是靠形容词堆出情绪。",
  },
  {
    icon: "ri-shield-check-line",
    title: "边界清晰",
    text: "本站不提供在线播放与下载，也不做资源导航。所有内容仅用于影迷交流、资料查阅与文化讨论。",
  },
];

export default function About() {
  usePageMeta(
    "关于我们 - 80s电影",
    "了解 80s电影的内容定位、编辑原则与联系方式。我们专注八十年代经典影片的资料整理与影评写作，不提供影片播放与下载。",
    "关于80s电影,电影资料站,影评编辑原则,八十年代电影,怀旧电影"
  );

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-page px-4 py-9 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
            关于 80s电影
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            一个由影迷维护的八十年代电影资料档案。我们想做的，是把那个年代的作品重新整理成可以慢慢读的东西。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              我们为什么做这个站
            </h2>
            <div className="mt-3 h-[1px] w-16 bg-accent-500"></div>
            <div className="mt-5 space-y-4 text-sm leading-[1.95] text-foreground-700 md:text-base">
              <p>
                八十年代是一个特别的十年：录像带让电影第一次可以被反复观看，合成器给了银幕新的声音，类型片在商业与作者表达之间找到了某种奇妙的平衡。很多作品在当年只是暑期档的一部片子，四十年后却成了整整一代人的共同记忆。
              </p>
              <p>
                但记忆是会变形的。我们常常记得一句台词、一段配乐、一个镜头，却忘了它哪一年上映、由谁执导、片长多少。80s电影想补上的，正是这些容易被忽略的部分——不是把怀旧做成滤镜，而是把影片重新放回它的时代里。
              </p>
              <p>
                目前站内收录了 {movies.length} 部影片与 {articles.length} 篇长文，覆盖 1980 至 1989 年。资料会持续补充，也欢迎你指出错误。
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="h-[260px] w-full overflow-hidden rounded-lg bg-secondary-200">
              <img
                src="https://readdy.ai/api/search-image?query=Empty%20vintage%20movie%20theatre%20with%20deep%20burgundy%20velvet%20seats%2C%20warm%20golden%20wall%20lamps%20and%20a%20glowing%20blank%20screen%2C%20painted%20retro%20cinematic%20illustration%2C%20soft%20dust%20in%20the%20air%2C%20film%20grain%2C%20cream%20and%20amber%20tones%2C%20no%20text&width=800&height=600&seq=about-cinema-seats&orientation=landscape"
                alt="八十年代电影放映厅"
                title="80s电影 关于我们"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                <p className="font-heading text-2xl font-semibold text-primary-700">{movies.length}</p>
                <p className="mt-1 text-xs text-secondary-600">收录影片</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                <p className="font-heading text-2xl font-semibold text-primary-700">{articles.length}</p>
                <p className="mt-1 text-xs text-secondary-600">影评与资讯</p>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-14">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
            编辑原则
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h3 className="mt-3 font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">
              内容范围与演示说明
            </h2>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
              <li className="flex gap-2.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                <span>影片资料、剧情概要与影评摘录，均依据公开信息与站内编辑整理。</span>
              </li>
              <li className="flex gap-2.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                <span>搜索、筛选、分页与收藏均为浏览器端演示，不连接后台，也不会保存到服务器。</span>
              </li>
              <li className="flex gap-2.5">
                <i className="ri-close-circle-line mt-0.5 text-secondary-500"></i>
                <span>本站不提供影片播放、下载或资源跳转链接，请通过正版渠道观看。</span>
              </li>
              <li className="flex gap-2.5">
                <i className="ri-close-circle-line mt-0.5 text-secondary-500"></i>
                <span>站内评论、评分仅作编辑部参考展示，非用户真实评价。</span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-accent-50/60 p-5 md:p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">
              联系与反馈
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如果你发现资料错误、想补充某部影片，或希望投稿影评，可以写信给我们。我们会在一周内回复。
            </p>
            <a
              href="mailto:archive@80sfilm.example"
              className="mt-4 flex items-center gap-3 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-mail-line"></i>
              </span>
              <span>
                <span className="block font-label text-[10px] uppercase tracking-[0.2em] text-secondary-600">
                  Email
                </span>
                <span className="text-sm text-foreground-900">archive@80sfilm.example</span>
              </span>
            </a>
            <p className="mt-3 text-[11px] leading-relaxed text-secondary-500">
              演示站点中的邮箱地址为示例地址，不会真实收信。
            </p>
            <Link
              to="/category"
              className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-700 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-800"
            >
              开始浏览影片
              <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
        </section>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-100/40 p-5 md:p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">
            版权与免责声明
          </h2>
          <div className="mt-3 space-y-3 text-xs leading-relaxed text-foreground-600 md:text-sm">
            <p>
              本站所有影片名称、海报概念图、剧照描述及相关商标，版权归原制片方与权利人所有。站内文字内容（影评、资料整理、栏目说明）由 80s电影编辑部撰写，转载请注明来源。
            </p>
            <p>
              配图均为站内原创插画，仅用于版面氛围示意，并非影片官方物料。若权利人认为内容存在不妥，请联系我们，我们会在核实后及时处理。
            </p>
            <p>© 2026 80s电影 · 本页面为展示型演示站点，不构成任何商业服务承诺。</p>
          </div>
        </section>
      </div>
    </div>
  );
}